import React, { useEffect, useMemo, useState } from "react";
import Header from "../../header/header";
import { Button, Checkbox, InputAdornment, LinearProgress, Pagination, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import { VideoAd } from "../../models";
import { getVideoAds, updateVideoAd } from "../../api";
import { searchService } from "../../scripts/search.service";
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useSnackbar } from "notistack";
import { VideoAdTableRow } from "../components/VideoAdTableRow";

const itemsPerPage = 15;

export const VideoAdListPage: React.FC = () => {
  const user = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();

  // Data state
  const [videoAds, setVideoAds] = useState<VideoAd[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  /// Search state (filtering & pagination)
  const [restore, setRestore] = useState(false);
  const [query, setQuery] = useState<string>(searchService.currentSearchValue?.query ?? '');
  const [page, setPage] = useState(searchService.currentSearchValue?.page ?? 1);
  const [selectedVideoAdIds, setSelectedVideoAdIds] = useState<number[]>([]);

  const searchVideoAds = useMemo(() => videoAds?.filter(video => {
    const queryMatch =
      video.name.toLowerCase().includes(query.toLowerCase()) ||
      video.title.toLowerCase().includes(query.toLowerCase());

    return queryMatch && (restore ? video.status === 'deleted' : video.status !== 'deleted');
  }), [videoAds, query, restore]);

  const selectedVideoAds = useMemo(() => searchVideoAds?.filter(video => selectedVideoAdIds.includes(video.id)), [searchVideoAds, selectedVideoAdIds]);
  const allSelected = useMemo(() => selectedVideoAds?.length === searchVideoAds?.length && (searchVideoAds?.length ?? 0) > 0, [selectedVideoAds, searchVideoAds]);

  const paginatedVideoAds = useMemo(() => searchVideoAds?.slice((page - 1) * itemsPerPage, page * itemsPerPage), [searchVideoAds, page]);

  // Derived state
  const loading = useMemo(() => !videoAds && !error, [videoAds, error]);

  // Save search state to local storage
  useEffect(() => searchService.setSearch({
    query,
    types: searchService.currentSearchValue?.types,
    page,
  }), [query, page]);

  // Reset page when query or types change
  useEffect(() => {
    setPage(1);
  }, [query]);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setError(null);

    try {
      const videoAds = await getVideoAds();
      setVideoAds(videoAds);
    } catch (error) {
      setError(error as string);
    }
  }

  function handleVideoAdSelect(video: VideoAd) {
    if (selectedVideoAdIds.includes(video.id)) {
      setSelectedVideoAdIds(selectedVideoAdIds.filter(id => id !== video.id));
    } else {
      setSelectedVideoAdIds([...selectedVideoAdIds, video.id]);
    }
  }

  function handleSelectAll() {
    if (allSelected) {
      setSelectedVideoAdIds([]);
    } else {
      setSelectedVideoAdIds(searchVideoAds?.map(video => video.id) ?? []);
    }
  }

  async function handleDeleteSelectedVideoAds() {
    enqueueSnackbar("Deleting selected video ads", { variant: "info" });

    try {
      const selectedVideoAds = searchVideoAds?.filter(persona => selectedVideoAdIds.includes(persona.id)) ?? [];
      await Promise.all(selectedVideoAds.map(persona => updateVideoAd(persona.id, {
        status: "deleted"
      })));
      enqueueSnackbar("Selected video ads deleted", { variant: "success" });
      setSelectedVideoAdIds([]);
      await fetchData();
    } catch (e) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
      console.error(e)
    }
  }

  async function handleRestoreSelectedVideoAds() {
    enqueueSnackbar("Restoring selected video ads", { variant: "info" });

    try {
      const selectedVideoAds = searchVideoAds?.filter(persona => selectedVideoAdIds.includes(persona.id)) ?? [];
      await Promise.all(selectedVideoAds.map(persona => updateVideoAd(persona.id, {
        status: "active"
      })));
      enqueueSnackbar("Selected video ads restored", { variant: "success" });
      setSelectedVideoAdIds([]);
      await fetchData();
    } catch (e) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
      console.error(e)
    }
  }

  return (
    <>
      <Header />
      {!loading && !error && <div className="content-header" style={{ top: "0.5rem", width: "90%", left: "6.25rem" }}>
        <div className="left" style={{ display: "flex" }}>
          <TextField
            label="Search"
            style={{ marginRight: "10px" }}
            onChange={(e) => setQuery(e.target.value)}
            focused
            size="small"
            value={query}
            className="foreInputWhite"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className="right" style={{ display: "flex" }}>
          <Link to={`/content/new-video-ads/new`}><Button style={{ marginRight: "10px" }} variant="contained" color="primary" className="spacer">New</Button></Link>
          {restore
            ? <Button variant="contained" color="primary" onClick={() => setRestore(false)} className="spacer">Normal</Button>
            : <Button disabled={!user?.perms.c3} variant="contained" color="primary" onClick={() => setRestore(true)} className="spacer">Restore</Button>
          }
          <Link to="/"><Button style={{ marginLeft: "10px" }} variant="contained" color="primary" className="spacer">Back</Button></Link>
        </div>
      </div>}
      {loading && <div style={{ width: "50%" }}>
        <LinearProgress />
      </div>}
      {error && <div>Error: {error}</div>}
      {!loading && !error && <div className="profiles-container" style={{ width: "70%", top: "1rem", maxHeight: "80vh" }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead className='table_head'>
              <TableRow>
                <TableCell className="table_cell top_row" style={{ width: "20%" }}>
                  <div style={{ display: "flex" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ fontWeight: "600" }}>Select All</span>
                      <Checkbox className="table_cell top_row" checked={allSelected} onChange={handleSelectAll}></Checkbox>
                    </div>
                    {(selectedVideoAds?.length ?? 0) > 0 && (
                      <>
                        {restore ?
                          <Button variant="contained" color="secondary" size="small" onClick={handleRestoreSelectedVideoAds} style={{ padding: "0.25rem", width: "58%", height: "90%", top: "2.5rem" }}>RESTORE SELECTED</Button> :
                          <Button variant="contained" color="secondary" size="small" onClick={handleDeleteSelectedVideoAds} style={{ padding: "0.25rem", width: "58%", height: "90%", top: "2.5rem" }}>DELETE SELECTED</Button>
                        }
                      </>
                    )}
                  </div>


                </TableCell>
                {/* <TableCell className="table_cell top_row" style={{width: "10%"}}></TableCell> */}
                <TableCell className="table_cell top_row" style={{ fontWeight: "600", width: "45%" }}>Name</TableCell>
                <TableCell className="table_cell top_row" style={{ fontWeight: "600", width: "15%" }}>Date Created</TableCell>
                <TableCell className="table_cell top_row" style={{ fontWeight: "600" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedVideoAds?.map((videoAd, index) => (
                <VideoAdTableRow
                  key={videoAd.id}
                  videoAd={videoAd}
                  selected={selectedVideoAds?.includes(videoAd) ?? false}
                  dark={index % 2 === 0}
                  onVideoAdSelect={handleVideoAdSelect}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      }
      {!loading && !error && <div className="pagination-container">
        <Pagination color="secondary" count={Math.ceil((searchVideoAds?.length ?? 0) / itemsPerPage)} page={page} onChange={(_, page) => setPage(page)} />
      </div>}
    </>
  );
};
